import React, { useState, useEffect, useCallback } from 'react';
import {
    Container, Typography, Box, Paper, Grid,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, InputLabel, Select, MenuItem,
    CircularProgress, Pagination, Chip, Alert, useTheme,
    Switch, FormControlLabel, Tabs, Tab,
    TextField, IconButton, Tooltip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import PeopleIcon from '@mui/icons-material/People';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import RateReviewIcon from '@mui/icons-material/RateReview';
import SpeedIcon from '@mui/icons-material/Speed';
import BoltIcon from '@mui/icons-material/Bolt';
import ShareIcon from '@mui/icons-material/Share';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LinkIcon from '@mui/icons-material/Link';
import { adminAPI, reviewBooksAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getAllThirdPartyBooks, setBookVisibility } from '../data/books';

const _API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const _SERVER_BASE_URL = _API_BASE_URL.replace(/\/api$/, '');
function resolveCoverUrl(coverImage) {
    if (!coverImage) return null;
    if (coverImage.startsWith('http://') || coverImage.startsWith('https://')) return coverImage;
    if (coverImage.startsWith('/uploads/')) return `${_SERVER_BASE_URL}${coverImage}`;
    return coverImage;
}

const cyan = '#00BFFF';

// ── Stat Card ────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, sub, color = cyan }) => {
    const theme = useTheme();
    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                gap: 2.5,
            }}
        >
            <Box
                sx={{
                    width: 52, height: 52, borderRadius: 2,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: alpha(color, 0.12),
                }}
            >
                <Icon sx={{ fontSize: 28, color }} />
            </Box>
            <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                    {value}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
                    {label}
                </Typography>
                {sub && (
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                        {sub}
                    </Typography>
                )}
            </Box>
        </Paper>
    );
};

// ── Subscription chip ────────────────────────────────────────────
const StatusChip = ({ status }) => {
    if (!status || status === 'free') return <Chip label="Free" size="small" />;
    const map = { active: 'success', trialing: 'info', canceled: 'error', unpaid: 'warning' };
    return <Chip label={status} color={map[status] || 'default'} size="small" />;
};

const SourceCell = ({ attribution }) => {
    const source = attribution?.conversionSource || attribution?.firstTouchSource;
    if (!source) return <Typography variant="caption" sx={{ color: 'text.disabled' }}>—</Typography>;
    return <Chip label={source} size="small" variant="outlined" />;
};

const PlanCell = ({ subscription }) => {
    if (!subscription?.planType) return <Typography variant="caption" sx={{ color: 'text.disabled' }}>Free</Typography>;
    const label = subscription.planType.charAt(0).toUpperCase() + subscription.planType.slice(1);
    const cycle = subscription.billingCycle ? ` (${subscription.billingCycle})` : '';
    return <Typography variant="body2">{label}{cycle}</Typography>;
};

// ── Main Component ───────────────────────────────────────────────
const AdminPanel = () => {
    const theme = useTheme();
    const { user, refreshUserData } = useAuth();
    const navigate = useNavigate();

    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Email stats
    const [emailStats, setEmailStats] = useState(null);
    const [emailStatsLoading, setEmailStatsLoading] = useState(false);
    const [emailStatsDays, setEmailStatsDays] = useState(30);

    // FLEX stats
    const [flexStats, setFlexStats] = useState(null);
    const [flexStatsLoading, setFlexStatsLoading] = useState(false);
    const [flexStatsDays, setFlexStatsDays] = useState(30);

    // Speed test stats
    const [speedStats, setSpeedStats] = useState(null);
    const [speedStatsLoading, setSpeedStatsLoading] = useState(false);
    const [speedStatsDays, setSpeedStatsDays] = useState(30);

    // Post-signup journey
    const [journeyData, setJourneyData] = useState(null);
    const [journeyLoading, setJourneyLoading] = useState(false);
    const [journeyDays, setJourneyDays] = useState(30);

    // Stats
    const [stats, setStats] = useState(null);

    // Users table
    const [users, setUsers] = useState([]);
    const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });

    // Subscription dialog
    const [selectedUser, setSelectedUser] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [subData, setSubData] = useState({ status: 'active', planType: 'basic', billingCycle: 'monthly', autoRenew: true });

    // Coupons
    const [coupons, setCoupons] = useState([]);
    const [couponForm, setCouponForm] = useState({ count: 1, durationDays: 30, affiliateUsername: '', note: '' });
    const [couponLoading, setCouponLoading] = useState(false);
    const [couponFilter, setCouponFilter] = useState('');
    const [copiedCode, setCopiedCode] = useState(null);

    // ── Auth guard ───────────────────────────────────────────────
    useEffect(() => {
        if (!user || user.role !== 'admin') {
            navigate('/');
        }
    }, [user, navigate]);

    // ── Fetch stats ──────────────────────────────────────────────
    const fetchStats = useCallback(async () => {
        try {
            const data = await adminAPI.getStats();
            setStats(data);
        } catch (err) {
            setError('Failed to load dashboard stats.');
        }
    }, []);

    // ── Fetch users ──────────────────────────────────────────────
    const fetchUsers = useCallback(async (page = 1) => {
        try {
            setLoading(true);
            const data = await adminAPI.getUsers(page, 15);
            setUsers(data.users);
            setPagination(data.pagination);
        } catch (err) {
            setError('Failed to load users.');
        } finally {
            setLoading(false);
        }
    }, []);

    // ── Fetch coupons ────────────────────────────────────────────
    const fetchCoupons = useCallback(async (status) => {
        try {
            const data = await adminAPI.getCoupons(status || undefined);
            setCoupons(data.coupons);
        } catch (err) {
            setError('Failed to load coupons.');
        }
    }, []);

    useEffect(() => {
        if (user && user.role === 'admin') {
            fetchStats();
            fetchUsers();
            fetchCoupons();
        }
    }, [user, fetchStats, fetchUsers, fetchCoupons]);

    // ── Fetch email stats ───────────────────────────────────────
    const fetchEmailStats = useCallback(async (days = emailStatsDays) => {
        try {
            setEmailStatsLoading(true);
            const data = await adminAPI.getEmailStats(days);
            setEmailStats(data);
        } catch (err) {
            console.error('Failed to fetch email stats:', err);
        } finally {
            setEmailStatsLoading(false);
        }
    }, [emailStatsDays]);

    useEffect(() => {
        if (user && user.role === 'admin' && tab === 3) {
            fetchEmailStats();
        }
    }, [user, tab, fetchEmailStats]);

    // ── Fetch FLEX stats ────────────────────────────────────────
    const fetchFlexStats = useCallback(async (days = flexStatsDays) => {
        try {
            setFlexStatsLoading(true);
            const data = await adminAPI.getFlexStats(days);
            setFlexStats(data);
        } catch (err) {
            console.error('Failed to fetch FLEX stats:', err);
        } finally {
            setFlexStatsLoading(false);
        }
    }, [flexStatsDays]);

    useEffect(() => {
        if (user && user.role === 'admin' && tab === 6) {
            fetchFlexStats();
        }
    }, [user, tab, fetchFlexStats]);

    // ── Fetch speed test stats ──────────────────────────────────
    const fetchSpeedStats = useCallback(async (days = speedStatsDays) => {
        try {
            setSpeedStatsLoading(true);
            const data = await adminAPI.getSpeedTestStats(days);
            setSpeedStats(data);
        } catch (err) {
            console.error('Failed to fetch speed test stats:', err);
        } finally {
            setSpeedStatsLoading(false);
        }
    }, [speedStatsDays]);

    useEffect(() => {
        if (user && user.role === 'admin' && tab === 7) {
            fetchSpeedStats();
        }
    }, [user, tab, fetchSpeedStats]);

    // ── Fetch post-signup journey ───────────────────────────────
    const fetchJourneyData = useCallback(async (days = journeyDays) => {
        try {
            setJourneyLoading(true);
            const data = await adminAPI.getPostSignupJourney(days);
            setJourneyData(data);
        } catch (err) {
            console.error('Failed to fetch journey data:', err);
        } finally {
            setJourneyLoading(false);
        }
    }, [journeyDays]);

    useEffect(() => {
        if (user && user.role === 'admin' && tab === 8) {
            fetchJourneyData();
        }
    }, [user, tab, fetchJourneyData]);

    // ── Helpers ──────────────────────────────────────────────────
    const fmt = (d) => d ? new Date(d).toLocaleDateString() : 'N/A';
    const money = (n) => `$${Number(n).toFixed(2)}`;

    // ── Subscription dialog ──────────────────────────────────────
    const openSubDialog = (u) => {
        setSelectedUser(u);
        setSubData({
            status: u.subscription?.status || 'active',
            planType: u.subscription?.planType || 'basic',
            billingCycle: u.subscription?.billingCycle || 'monthly',
            autoRenew: u.subscription?.autoRenew !== false,
        });
        setOpenDialog(true);
    };

    const handleUpdateSub = async () => {
        try {
            setLoading(true);
            await adminAPI.updateUserSubscription(selectedUser._id, subData);
            setUsers(prev => prev.map(u =>
                u._id === selectedUser._id
                    ? { ...u, subscription: { ...u.subscription, ...subData } }
                    : u
            ));
            if (user && selectedUser && user._id === selectedUser._id) await refreshUserData();
            setOpenDialog(false);
            setError(null);
        } catch (err) {
            setError('Failed to update subscription.');
        } finally {
            setLoading(false);
        }
    };

    // ── Guard render ─────────────────────────────────────────────
    if (!user || user.role !== 'admin') {
        return (
            <Container maxWidth="md" sx={{ mt: 8 }}>
                <Alert severity="error">Access denied.</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                Control Panel
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                Platform overview and user management.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                variant="scrollable"
                scrollButtons
                allowScrollButtonsMobile
                sx={{
                    mb: 3,
                    '& .MuiTabs-scroller': {
                        overflowX: 'auto !important',
                        '&::-webkit-scrollbar': { display: 'none' },
                        scrollbarWidth: 'none',
                    },
                    '& .MuiTabs-scrollButtons': { display: 'flex !important' },
                    '& .MuiTabs-scrollButtons.Mui-disabled': { opacity: 0.3 },
                    '& .MuiTab-root': { minWidth: 'auto' },
                }}
            >
                <Tab label="Dashboard" />
                <Tab label="Users" />
                <Tab label="Coupons" />
                <Tab label="Emails" />
                <Tab label="Books" />
                <Tab label="Review Books" />
                <Tab label="FLEX" />
                <Tab label="Speed Test" />
                <Tab label="User Journey" />
            </Tabs>

            {/* ═══════════ TAB 0: DASHBOARD ═══════════ */}
            {tab === 0 && (
                <>
                    {!stats ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
                    ) : (
                        <>
                            {/* KPI Cards */}
                            <Grid container spacing={2.5} sx={{ mb: 4 }}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <StatCard icon={PeopleIcon} label="Total Users" value={stats.users.total} sub={`+${stats.users.newThisMonth} this month`} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <StatCard icon={PersonAddIcon} label="New This Month" value={stats.users.newThisMonth} sub={stats.users.growthPercent !== 0 ? `${stats.users.growthPercent > 0 ? '+' : ''}${stats.users.growthPercent}% vs last month` : 'Same as last month'} color="#8DC63F" />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <StatCard icon={AttachMoneyIcon} label="Total Revenue" value={money(stats.revenue.total)} sub={`${money(stats.revenue.thisMonth)} this month`} color="#FFB300" />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <StatCard icon={CreditCardIcon} label="Active Subs" value={stats.subscriptions.byStatus.active || 0} sub={`${stats.subscriptions.byStatus.trialing || 0} trialing`} color="#AB47BC" />
                                </Grid>
                            </Grid>

                            {/* Subscription Breakdown + Sign-up Trend side by side */}
                            <Grid container spacing={2.5} sx={{ mb: 4 }}>
                                {/* Status breakdown */}
                                <Grid item xs={12} md={4}>
                                    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Subscription Status</Typography>
                                        {Object.entries(stats.subscriptions.byStatus).map(([status, count]) => (
                                            <Box key={status} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{status}</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{count}</Typography>
                                            </Box>
                                        ))}
                                    </Paper>
                                </Grid>

                                {/* Plan breakdown */}
                                <Grid item xs={12} md={4}>
                                    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Plan Breakdown</Typography>
                                        {Object.keys(stats.subscriptions.byPlan).length === 0 ? (
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>No paying subscribers yet.</Typography>
                                        ) : (
                                            Object.entries(stats.subscriptions.byPlan).map(([plan, count]) => (
                                                <Box key={plan} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{plan}</Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{count}</Typography>
                                                </Box>
                                            ))
                                        )}
                                        {Object.keys(stats.subscriptions.byCycle).length > 0 && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>Billing Cycle</Typography>
                                                {Object.entries(stats.subscriptions.byCycle).map(([cycle, count]) => (
                                                    <Box key={cycle} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{cycle}</Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{count}</Typography>
                                                    </Box>
                                                ))}
                                            </>
                                        )}
                                    </Paper>
                                </Grid>

                                {/* Sign-up trend */}
                                <Grid item xs={12} md={4}>
                                    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Sign-up Trend (6mo)</Typography>
                                        {stats.signupTrend.length === 0 ? (
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>No sign-up data yet.</Typography>
                                        ) : (
                                            stats.signupTrend.map((m) => (
                                                <Box key={m.month} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                                    <Typography variant="body2" sx={{ minWidth: 72 }}>{m.month}</Typography>
                                                    <Box sx={{ flex: 1, height: 8, borderRadius: 1, bgcolor: alpha(cyan, 0.15), overflow: 'hidden' }}>
                                                        <Box sx={{
                                                            height: '100%', borderRadius: 1, bgcolor: cyan,
                                                            width: `${Math.min(100, (m.count / Math.max(...stats.signupTrend.map(s => s.count), 1)) * 100)}%`,
                                                            transition: 'width 0.5s',
                                                        }} />
                                                    </Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 24, textAlign: 'right' }}>{m.count}</Typography>
                                                </Box>
                                            ))
                                        )}
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* Recent Users */}
                            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Recent Sign-ups</Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Username</TableCell>
                                                <TableCell>Email</TableCell>
                                                <TableCell>Joined</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell>Plan</TableCell>
                                                <TableCell>Source</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {stats.recentUsers.map((u) => (
                                                <TableRow key={u._id}>
                                                    <TableCell sx={{ fontWeight: 600 }}>{u.username}</TableCell>
                                                    <TableCell>{u.email}</TableCell>
                                                    <TableCell>{fmt(u.createdAt)}</TableCell>
                                                    <TableCell><StatusChip status={u.subscription?.status} /></TableCell>
                                                    <TableCell><PlanCell subscription={u.subscription} /></TableCell>
                                                    <TableCell><SourceCell attribution={u.attribution} /></TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </>
                    )}
                </>
            )}

            {/* ═══════════ TAB 1: USERS ═══════════ */}
            {tab === 1 && (
                <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                    {loading && users.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
                    ) : (
                        <>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Username</TableCell>
                                            <TableCell>Email</TableCell>
                                            <TableCell>Joined</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Plan</TableCell>
                                            <TableCell>Source</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {users.map(u => (
                                            <TableRow key={u._id} hover>
                                                <TableCell sx={{ fontWeight: 600 }}>{u.username}</TableCell>
                                                <TableCell>{u.email}</TableCell>
                                                <TableCell>{fmt(u.createdAt)}</TableCell>
                                                <TableCell><StatusChip status={u.subscription?.status} /></TableCell>
                                                <TableCell><PlanCell subscription={u.subscription} /></TableCell>
                                                <TableCell><SourceCell attribution={u.attribution} /></TableCell>
                                                <TableCell align="right">
                                                    <Button variant="outlined" size="small" onClick={() => openSubDialog(u)}>
                                                        Manage
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                <Pagination
                                    count={pagination.pages}
                                    page={pagination.page}
                                    onChange={(_, v) => { setPagination(prev => ({ ...prev, page: v })); fetchUsers(v); }}
                                    color="primary"
                                />
                            </Box>
                        </>
                    )}
                </Paper>
            )}

            {/* ═══════════ TAB 2: COUPONS ═══════════ */}
            {tab === 2 && (
                <Grid container spacing={2.5}>
                    {/* Generate coupons */}
                    <Grid item xs={12} md={4}>
                        <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Generate Coupons</Typography>
                            <TextField fullWidth size="small" margin="dense" label="Count" type="number" inputProps={{ min: 1, max: 50 }} value={couponForm.count} onChange={(e) => setCouponForm(p => ({ ...p, count: parseInt(e.target.value) || 1 }))} />
                            <TextField fullWidth size="small" margin="dense" label="Duration (days)" type="number" inputProps={{ min: 1, max: 365 }} value={couponForm.durationDays} onChange={(e) => setCouponForm(p => ({ ...p, durationDays: parseInt(e.target.value) || 30 }))} />
                            <TextField fullWidth size="small" margin="dense" label="Affiliate Username (optional)" value={couponForm.affiliateUsername} onChange={(e) => setCouponForm(p => ({ ...p, affiliateUsername: e.target.value }))} />
                            <TextField fullWidth size="small" margin="dense" label="Note (optional)" value={couponForm.note} onChange={(e) => setCouponForm(p => ({ ...p, note: e.target.value }))} />
                            <Button
                                fullWidth variant="contained" sx={{ mt: 2 }}
                                disabled={couponLoading}
                                onClick={async () => {
                                    try {
                                        setCouponLoading(true);
                                        await adminAPI.createCoupons(couponForm);
                                        fetchCoupons(couponFilter);
                                    } catch (err) {
                                        setError(err.response?.data?.message || 'Failed to create coupons.');
                                    } finally {
                                        setCouponLoading(false);
                                    }
                                }}
                            >
                                {couponLoading ? <CircularProgress size={22} /> : `Generate ${couponForm.count} Coupon(s)`}
                            </Button>
                        </Paper>
                    </Grid>

                    {/* Coupon list */}
                    <Grid item xs={12} md={8}>
                        <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>All Coupons ({coupons.length})</Typography>
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <Select displayEmpty value={couponFilter} onChange={(e) => { setCouponFilter(e.target.value); fetchCoupons(e.target.value); }}>
                                        <MenuItem value="">All</MenuItem>
                                        <MenuItem value="active">Active</MenuItem>
                                        <MenuItem value="redeemed">Redeemed</MenuItem>
                                        <MenuItem value="revoked">Revoked</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Code</TableCell>
                                            <TableCell>Days</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Redeemed By</TableCell>
                                            <TableCell>Redeemed On</TableCell>
                                            <TableCell>Expires</TableCell>
                                            <TableCell>Affiliate</TableCell>
                                            <TableCell>Note</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {coupons.map(c => (
                                            <TableRow key={c._id} hover>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{c.code}</Typography>
                                                        <Tooltip title={copiedCode === c.code ? 'Copied!' : 'Copy'}>
                                                            <IconButton size="small" onClick={() => { navigator.clipboard.writeText(c.code); setCopiedCode(c.code); setTimeout(() => setCopiedCode(null), 1500); }}>
                                                                <ContentCopyIcon sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{c.grant?.durationDays}</TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                                                        <Chip size="small" label={c.status}
                                                            color={c.status === 'active' ? 'success' : c.status === 'redeemed' ? 'info' : 'default'}
                                                        />
                                                        {c.status === 'redeemed' && c.expired && (
                                                            <Chip size="small" label="Expired" color="warning" variant="outlined" />
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{c.redeemedBy?.username || '—'}</TableCell>
                                                <TableCell>{c.redeemedAt ? fmt(c.redeemedAt) : '—'}</TableCell>
                                                <TableCell>
                                                    {c.expiresAt ? (
                                                        <Box>
                                                            <Typography variant="body2">{fmt(c.expiresAt)}</Typography>
                                                            <Typography variant="caption" sx={{ color: c.expired ? 'warning.main' : 'text.secondary' }}>
                                                                {c.expired ? 'Expired' : `${c.daysRemaining}d left`}
                                                            </Typography>
                                                        </Box>
                                                    ) : '—'}
                                                </TableCell>
                                                <TableCell>{c.affiliateUser?.username || '—'}</TableCell>
                                                <TableCell><Typography variant="caption" sx={{ color: 'text.secondary' }}>{c.note || '—'}</Typography></TableCell>
                                                <TableCell align="right">
                                                    {c.status === 'active' && (
                                                        <Button size="small" color="error" onClick={async () => {
                                                            try { await adminAPI.revokeCoupon(c._id); fetchCoupons(couponFilter); } catch (err) { setError('Failed to revoke.'); }
                                                        }}>Revoke</Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {coupons.length === 0 && (
                                            <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>No coupons yet.</TableCell></TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {/* ═══════════ TAB 3: EMAILS ═══════════ */}
            {tab === 3 && (
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Email Analytics</Typography>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                                value={emailStatsDays}
                                onChange={(e) => { setEmailStatsDays(e.target.value); fetchEmailStats(e.target.value); }}
                            >
                                <MenuItem value={7}>Last 7 days</MenuItem>
                                <MenuItem value={30}>Last 30 days</MenuItem>
                                <MenuItem value={90}>Last 90 days</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {emailStatsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
                    ) : emailStats ? (
                        <>
                            {/* KPI Cards */}
                            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <StatCard icon={EmailIcon} label="Total Sent" value={emailStats.totalSent} color="#667eea" />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <StatCard icon={EmailIcon} label="Open Rate" value={`${emailStats.openRate}%`} sub={`${emailStats.opens} opens`} color="#8DC63F" />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <StatCard icon={EmailIcon} label="Click Rate" value={`${emailStats.clickRate}%`} sub={`${emailStats.clicks} clicks`} color="#00BFFF" />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <StatCard icon={EmailIcon} label="Bounce Rate" value={`${emailStats.bounceRate}%`} sub={`${emailStats.bounces} bounces, ${emailStats.complaints} complaints`} color="#f44336" />
                                </Grid>
                            </Grid>

                            {/* By Type */}
                            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, mb: 2.5 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Emails by Type</Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Type</TableCell>
                                                <TableCell align="right">Count</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {emailStats.byType.map((t) => (
                                                <TableRow key={t.type}>
                                                    <TableCell sx={{ textTransform: 'capitalize' }}>{t.type.replace(/_/g, ' ')}</TableCell>
                                                    <TableCell align="right">{t.count}</TableCell>
                                                </TableRow>
                                            ))}
                                            {emailStats.byType.length === 0 && (
                                                <TableRow><TableCell colSpan={2} align="center" sx={{ py: 3, color: 'text.secondary' }}>No emails sent yet.</TableCell></TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>

                            {/* By Category */}
                            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Emails by Category</Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Category</TableCell>
                                                <TableCell align="right">Count</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {emailStats.byCategory.map((c) => (
                                                <TableRow key={c.category}>
                                                    <TableCell sx={{ textTransform: 'capitalize' }}>{c.category}</TableCell>
                                                    <TableCell align="right">{c.count}</TableCell>
                                                </TableRow>
                                            ))}
                                            {emailStats.byCategory.length === 0 && (
                                                <TableRow><TableCell colSpan={2} align="center" sx={{ py: 3, color: 'text.secondary' }}>No emails sent yet.</TableCell></TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </>
                    ) : (
                        <Alert severity="info">No email data available yet. Emails will appear here once you start sending them.</Alert>
                    )}
                </Box>
            )}

            {/* ═══════════ SUBSCRIPTION DIALOG ═══════════ */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Update Subscription
                    {selectedUser && (
                        <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                            {selectedUser.username} ({selectedUser.email})
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Status</InputLabel>
                            <Select name="status" value={subData.status} onChange={(e) => setSubData(p => ({ ...p, status: e.target.value }))} label="Status">
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="trialing">Trial</MenuItem>
                                <MenuItem value="canceled">Canceled</MenuItem>
                                <MenuItem value="unpaid">Unpaid</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Plan</InputLabel>
                            <Select name="planType" value={subData.planType} onChange={(e) => setSubData(p => ({ ...p, planType: e.target.value }))} label="Plan">
                                <MenuItem value="basic">Basic</MenuItem>
                                <MenuItem value="pro">Pro</MenuItem>
                                <MenuItem value="team">Team</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Billing</InputLabel>
                            <Select name="billingCycle" value={subData.billingCycle} onChange={(e) => setSubData(p => ({ ...p, billingCycle: e.target.value }))} label="Billing">
                                <MenuItem value="monthly">Monthly</MenuItem>
                                <MenuItem value="annual">Annual</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControlLabel
                            control={<Switch checked={subData.autoRenew !== false} onChange={(e) => setSubData(p => ({ ...p, autoRenew: e.target.checked }))} />}
                            label="Auto-Renewal"
                            sx={{ mt: 2 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button onClick={handleUpdateSub} variant="contained" disabled={loading}>
                        {loading ? <CircularProgress size={24} /> : 'Update'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ═══════════ TAB 4: BOOKS ═══════════ */}
            {tab === 4 && (
                <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                        <MenuBookIcon color="primary" />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Affiliate Book Visibility
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Toggle which affiliate books are visible on the public Books page. ReaderFLEX Originals are always shown.
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Title</TableCell>
                                    <TableCell>Author</TableCell>
                                    <TableCell>Category</TableCell>
                                    <TableCell align="center">Visible</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {getAllThirdPartyBooks().map((book) => (
                                    <TableRow key={book.id} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>{book.title}</TableCell>
                                        <TableCell>{book.author}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={book.categories?.[0] ? book.categories[0].replace(/-/g, ' ') : '—'}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Switch
                                                checked={book.visible !== false}
                                                onChange={(e) => {
                                                    setBookVisibility(book.id, e.target.checked);
                                                    // Force re-render
                                                    setTab(4);
                                                }}
                                                size="small"
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Alert severity="info" sx={{ mt: 2 }}>
                        Changes are in-memory only and reset on page reload. To permanently change visibility, edit the <code>visible</code> field in <code>client/src/data/books.js</code>.
                    </Alert>
                </Paper>
            )}

            {/* ═══════════ TAB 5: REVIEW BOOKS ═══════════ */}
            {tab === 5 && <ReviewBooksManager />}

            {/* ═══════════ TAB 6: FLEX DASHBOARD ═══════════ */}
            {tab === 6 && (
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <BoltIcon sx={{ color: '#FFD700' }} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                FLEX Social Loop Analytics
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {[7, 30, 90].map(d => (
                                <Button
                                    key={d}
                                    size="small"
                                    variant={flexStatsDays === d ? 'contained' : 'outlined'}
                                    onClick={() => { setFlexStatsDays(d); fetchFlexStats(d); }}
                                >
                                    {d}d
                                </Button>
                            ))}
                        </Box>
                    </Box>

                    {flexStatsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
                    ) : !flexStats ? (
                        <Alert severity="info">No FLEX data available yet.</Alert>
                    ) : (
                        <>
                            {/* KPI Cards */}
                            <Grid container spacing={2.5} sx={{ mb: 4 }}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <StatCard icon={BoltIcon} label="Cards Generated" value={flexStats.cards.lastN} sub={`${flexStats.cards.total} all-time`} color="#FFD700" />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <StatCard icon={ShareIcon} label="Cards Shared" value={flexStats.cards.sharesLastN} sub={`${flexStats.viral.sharesPerUser} per user`} color="#00BFFF" />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <StatCard icon={EmojiEventsIcon} label="Challenges" value={flexStats.challenges.lastN} sub={`${flexStats.challenges.total} all-time`} color="#8DC63F" />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <StatCard icon={LinkIcon} label="Link Clicks" value={flexStats.shortLinks.totalClicksLastN} sub={`${(flexStats.viral.clickThroughRate * 100).toFixed(1)}% CTR`} color="#FF9800" />
                                </Grid>
                            </Grid>

                            {/* Viral Coefficient */}
                            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                    <TrendingUpIcon color="primary" />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        Viral Coefficient (k)
                                    </Typography>
                                </Box>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h3" sx={{ fontWeight: 800, color: flexStats.viral.viralCoefficient >= 1 ? 'success.main' : 'text.primary' }}>
                                                {flexStats.viral.viralCoefficient}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                k = shares × CTR × conversion
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h5" sx={{ fontWeight: 700 }}>{flexStats.viral.sharesPerUser}</Typography>
                                            <Typography variant="caption" color="text.secondary">Shares / User</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h5" sx={{ fontWeight: 700 }}>{(flexStats.viral.clickThroughRate * 100).toFixed(1)}%</Typography>
                                            <Typography variant="caption" color="text.secondary">Click-Through Rate</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h5" sx={{ fontWeight: 700 }}>{(flexStats.viral.conversionRate * 100).toFixed(1)}%</Typography>
                                            <Typography variant="caption" color="text.secondary">Conversion Rate</Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                                <Alert severity={flexStats.viral.viralCoefficient >= 1 ? 'success' : 'info'} sx={{ mt: 2 }}>
                                    {flexStats.viral.viralCoefficient >= 1
                                        ? 'Viral coefficient ≥ 1 — the loop is self-sustaining!'
                                        : 'k < 1 — each user brings in fewer than one new user. Tune share triggers, landing pages, or CTAs.'}
                                </Alert>
                            </Paper>

                            {/* Funnel */}
                            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                                    Viral Funnel (last {flexStatsDays} days)
                                </Typography>
                                {[
                                    { label: 'Cards Generated', value: flexStats.funnel.cardsGenerated, icon: BoltIcon, color: '#FFD700' },
                                    { label: 'Cards Shared', value: flexStats.funnel.cardsShared, icon: ShareIcon, color: '#00BFFF' },
                                    { label: 'Link Clicks', value: flexStats.funnel.linkClicks, icon: LinkIcon, color: '#FF9800' },
                                    { label: 'Guest Challenges', value: flexStats.funnel.guestChallenges, icon: EmojiEventsIcon, color: '#8DC63F' },
                                    { label: 'Guest → Account Merges', value: flexStats.funnel.guestMerges, icon: PersonAddIcon, color: '#9C27B0' },
                                ].map((step, i, arr) => {
                                    const maxVal = arr[0].value || 1;
                                    const pct = maxVal > 0 ? (step.value / maxVal) * 100 : 0;
                                    const prevPct = i > 0 && arr[i - 1].value > 0 ? (step.value / arr[i - 1].value) * 100 : 100;
                                    return (
                                        <Box key={i} sx={{ mb: 1.5 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {step.label}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {step.value.toLocaleString()}
                                                    {i > 0 && step.value > 0 && (
                                                        <span style={{ marginLeft: 8, fontSize: '0.75rem', opacity: 0.7 }}>
                                                            ({prevPct.toFixed(0)}% of previous)
                                                        </span>
                                                    )}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ height: 24, bgcolor: 'rgba(0,0,0,0.06)', borderRadius: 1, overflow: 'hidden' }}>
                                                <Box sx={{
                                                    height: '100%',
                                                    width: `${pct}%`,
                                                    bgcolor: step.color,
                                                    borderRadius: 1,
                                                    transition: 'width 0.5s ease',
                                                }} />
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Paper>

                            {/* Cards by Type & Share Platforms */}
                            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                                <Grid item xs={12} md={6}>
                                    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                                            Cards by Type (all-time)
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            {Object.entries(flexStats.cards.byType).map(([type, count]) => (
                                                <Chip key={type} label={`${type}: ${count}`} size="small" variant="outlined" />
                                            ))}
                                            {Object.keys(flexStats.cards.byType).length === 0 && (
                                                <Typography variant="body2" color="text.secondary">No cards yet.</Typography>
                                            )}
                                        </Box>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                                            Share Platforms (last {flexStatsDays}d)
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            {Object.entries(flexStats.cards.sharesByPlatform).map(([platform, count]) => (
                                                <Chip key={platform} label={`${platform}: ${count}`} size="small" variant="outlined" color="primary" />
                                            ))}
                                            {Object.keys(flexStats.cards.sharesByPlatform).length === 0 && (
                                                <Typography variant="body2" color="text.secondary">No shares yet.</Typography>
                                            )}
                                        </Box>
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* Challenge Status & Outcomes */}
                            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                                <Grid item xs={12} md={6}>
                                    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                                            Challenge Status (last {flexStatsDays}d)
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            {Object.entries(flexStats.challenges.byStatus).map(([status, count]) => (
                                                <Chip
                                                    key={status}
                                                    label={`${status}: ${count}`}
                                                    size="small"
                                                    color={status === 'completed' ? 'success' : status === 'declined' || status === 'expired' ? 'error' : 'default'}
                                                    variant="outlined"
                                                />
                                            ))}
                                            {Object.keys(flexStats.challenges.byStatus).length === 0 && (
                                                <Typography variant="body2" color="text.secondary">No challenges yet.</Typography>
                                            )}
                                        </Box>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                                            Completed Outcomes (last {flexStatsDays}d)
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            {Object.entries(flexStats.challenges.byOutcome).map(([outcome, count]) => (
                                                <Chip key={outcome} label={`${outcome}: ${count}`} size="small" variant="outlined" />
                                            ))}
                                            {Object.keys(flexStats.challenges.byOutcome).length === 0 && (
                                                <Typography variant="body2" color="text.secondary">No completed challenges yet.</Typography>
                                            )}
                                        </Box>
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* Guest Conversion */}
                            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                                    Guest Challenge Conversion
                                </Typography>
                                <Grid container spacing={3}>
                                    <Grid item xs={4}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h5" sx={{ fontWeight: 700 }}>{flexStats.challenges.guestChallengesLastN}</Typography>
                                            <Typography variant="caption" color="text.secondary">Guest Challenges Sent</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h5" sx={{ fontWeight: 700 }}>{flexStats.challenges.guestMergesLastN}</Typography>
                                            <Typography variant="caption" color="text.secondary">Guest → Account Merges</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                                {flexStats.challenges.guestChallengesLastN > 0
                                                    ? `${((flexStats.challenges.guestMergesLastN / flexStats.challenges.guestChallengesLastN) * 100).toFixed(1)}%`
                                                    : '—'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">Merge Rate</Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>

                            {/* Time Series */}
                            {flexStats.timeSeries && flexStats.timeSeries.length > 0 && (
                                <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                                        Daily Activity (last {flexStatsDays}d)
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        {flexStats.timeSeries.slice(-30).map((day) => {
                                            const maxCards = Math.max(...flexStats.timeSeries.map(d => d.cards), 1);
                                            const maxChallenges = Math.max(...flexStats.timeSeries.map(d => d.challenges), 1);
                                            return (
                                                <Box key={day.date} sx={{ textAlign: 'center', minWidth: 40 }}>
                                                    <Box sx={{ height: 60, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 0.25 }}>
                                                        <Box sx={{ height: `${(day.challenges / maxChallenges) * 30}px`, bgcolor: '#8DC63F', borderRadius: 0.5, mx: 'auto', width: 20 }} title={`${day.challenges} challenges`} />
                                                        <Box sx={{ height: `${(day.cards / maxCards) * 30}px`, bgcolor: '#FFD700', borderRadius: 0.5, mx: 'auto', width: 20 }} title={`${day.cards} cards`} />
                                                    </Box>
                                                    <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>
                                                        {day.date.slice(5)}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 2, mt: 1, justifyContent: 'center' }}>
                                        <Chip label="Cards" size="small" sx={{ bgcolor: '#FFD700', color: '#000' }} />
                                        <Chip label="Challenges" size="small" sx={{ bgcolor: '#8DC63F', color: '#000' }} />
                                    </Box>
                                </Paper>
                            )}
                        </>
                    )}
                </Box>
            )}

            {/* ═══════════ TAB 7: SPEED TEST ANALYTICS ═══════════ */}
            {tab === 7 && (
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <SpeedIcon sx={{ color: cyan }} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                Speed Test Analytics
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {[7, 30, 90].map(d => (
                                <Button
                                    key={d}
                                    size="small"
                                    variant={speedStatsDays === d ? 'contained' : 'outlined'}
                                    onClick={() => { setSpeedStatsDays(d); fetchSpeedStats(d); }}
                                >
                                    {d}d
                                </Button>
                            ))}
                        </Box>
                    </Box>

                    {speedStatsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
                    ) : !speedStats ? (
                        <Alert severity="info">No speed test data available yet.</Alert>
                    ) : (
                        <>
                            {/* KPI Cards */}
                            <Grid container spacing={2.5} sx={{ mb: 4 }}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <StatCard icon={SpeedIcon} label="Assessments" value={speedStats.totals.lastN} sub={`${speedStats.totals.allTime} all-time`} color={cyan} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <StatCard icon={TrendingUpIcon} label="Avg WPM" value={speedStats.averages.wpm} sub={`Avg ${speedStats.averages.comprehension}% comprehension`} color="#8DC63F" />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <StatCard icon={EmailIcon} label="Emails Captured" value={speedStats.funnel.emailsCaptured} sub={`${speedStats.funnel.emailsCaptured > 0 ? ((speedStats.funnel.emailsCaptured / speedStats.totals.lastN) * 100).toFixed(0) : 0}% capture rate`} color="#FF9800" />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <StatCard icon={PersonAddIcon} label="Accounts Created" value={speedStats.funnel.accountsCreated} sub={`${speedStats.funnel.trialsStarted} trials started`} color="#9C27B0" />
                                </Grid>
                            </Grid>

                            {/* Conversion Funnel */}
                            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                                    Conversion Funnel (last {speedStatsDays}d)
                                </Typography>
                                {[
                                    { label: 'Assessments Completed', value: speedStats.funnel.assessmentsStarted, color: cyan },
                                    { label: 'Guided Comparison Tried', value: speedStats.funnel.guidedCompleted, color: '#7C6BFF' },
                                    { label: 'Email Captured (Blueprint)', value: speedStats.funnel.emailsCaptured, color: '#FF9800' },
                                    { label: 'Account Created', value: speedStats.funnel.accountsCreated, color: '#9C27B0' },
                                    { label: 'Trial Started', value: speedStats.funnel.trialsStarted, color: '#8DC63F' },
                                ].map((step, i, arr) => {
                                    const maxVal = arr[0].value || 1;
                                    const pct = maxVal > 0 ? (step.value / maxVal) * 100 : 0;
                                    const prevPct = i > 0 && arr[i - 1].value > 0 ? (step.value / arr[i - 1].value) * 100 : 100;
                                    return (
                                        <Box key={i} sx={{ mb: 1.5 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {step.label}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {step.value.toLocaleString()}
                                                    {i > 0 && step.value > 0 && (
                                                        <span style={{ marginLeft: 8, fontSize: '0.75rem', opacity: 0.7 }}>
                                                            ({prevPct.toFixed(0)}% of previous)
                                                        </span>
                                                    )}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ height: 24, bgcolor: 'rgba(0,0,0,0.06)', borderRadius: 1, overflow: 'hidden' }}>
                                                <Box sx={{
                                                    height: '100%',
                                                    width: `${pct}%`,
                                                    bgcolor: step.color,
                                                    borderRadius: 1,
                                                    transition: 'width 0.5s ease',
                                                }} />
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Paper>

                            {/* Averages + Distributions */}
                            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                                <Grid item xs={12} md={6}>
                                    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>WPM Distribution</Typography>
                                        {Object.entries(speedStats.distributions.wpmBands).map(([band, count]) => {
                                            const maxBand = Math.max(...Object.values(speedStats.distributions.wpmBands), 1);
                                            return (
                                                <Box key={band} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                                    <Typography variant="body2" sx={{ minWidth: 80 }}>{band}</Typography>
                                                    <Box sx={{ flex: 1, height: 8, borderRadius: 1, bgcolor: alpha(cyan, 0.15), overflow: 'hidden' }}>
                                                        <Box sx={{ height: '100%', borderRadius: 1, bgcolor: cyan, width: `${(count / maxBand) * 100}%`, transition: 'width 0.5s' }} />
                                                    </Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 24, textAlign: 'right' }}>{count}</Typography>
                                                </Box>
                                            );
                                        })}
                                        {Object.keys(speedStats.distributions.wpmBands).length === 0 && (
                                            <Typography variant="body2" color="text.secondary">No data yet.</Typography>
                                        )}
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Comprehension Distribution</Typography>
                                        {Object.entries(speedStats.distributions.comprehensionBands).map(([band, count]) => {
                                            const maxBand = Math.max(...Object.values(speedStats.distributions.comprehensionBands), 1);
                                            return (
                                                <Box key={band} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                                    <Typography variant="body2" sx={{ minWidth: 80 }}>{band}</Typography>
                                                    <Box sx={{ flex: 1, height: 8, borderRadius: 1, bgcolor: alpha('#8DC63F', 0.15), overflow: 'hidden' }}>
                                                        <Box sx={{ height: '100%', borderRadius: 1, bgcolor: '#8DC63F', width: `${(count / maxBand) * 100}%`, transition: 'width 0.5s' }} />
                                                    </Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 24, textAlign: 'right' }}>{count}</Typography>
                                                </Box>
                                            );
                                        })}
                                        {Object.keys(speedStats.distributions.comprehensionBands).length === 0 && (
                                            <Typography variant="body2" color="text.secondary">No data yet.</Typography>
                                        )}
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* Source + Intent Attribution */}
                            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                                <Grid item xs={12} md={6}>
                                    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Traffic Sources</Typography>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            {Object.entries(speedStats.attribution.sources).map(([src, count]) => (
                                                <Chip key={src} label={`${src}: ${count}`} size="small" variant="outlined" />
                                            ))}
                                            {Object.keys(speedStats.attribution.sources).length === 0 && (
                                                <Typography variant="body2" color="text.secondary">No source data yet.</Typography>
                                            )}
                                        </Box>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Landing Intent</Typography>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            {Object.entries(speedStats.attribution.intents).map(([intent, count]) => (
                                                <Chip key={intent} label={`${intent}: ${count}`} size="small" variant="outlined" color="primary" />
                                            ))}
                                            {Object.keys(speedStats.attribution.intents).length === 0 && (
                                                <Typography variant="body2" color="text.secondary">No intent data yet.</Typography>
                                            )}
                                        </Box>
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* Friction Profiles */}
                            {Object.keys(speedStats.distributions.frictionProfiles).length > 0 && (
                                <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Friction Profile Breakdown</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {Object.entries(speedStats.distributions.frictionProfiles).map(([id, data]) => (
                                            <Chip key={id} label={`${data.name}: ${data.count}`} size="small" variant="outlined" />
                                        ))}
                                    </Box>
                                </Paper>
                            )}

                            {/* Recent Assessments Table */}
                            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Recent Assessments</Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Date</TableCell>
                                                <TableCell>WPM</TableCell>
                                                <TableCell>Comp</TableCell>
                                                <TableCell>Eff WPM</TableCell>
                                                <TableCell>Guided</TableCell>
                                                <TableCell>Source</TableCell>
                                                <TableCell>State</TableCell>
                                                <TableCell>Merged</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {speedStats.recentAssessments.map((a) => (
                                                <TableRow key={a._id}>
                                                    <TableCell>{new Date(a.createdAt).toLocaleDateString()}</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>{a.baseline?.wpm || '—'}</TableCell>
                                                    <TableCell>{a.baseline?.comprehension?.percentage != null ? `${a.baseline.comprehension.percentage}%` : '—'}</TableCell>
                                                    <TableCell>{a.baseline?.effectiveWpm || '—'}</TableCell>
                                                    <TableCell>{a.guided?.wpm ? `${a.guided.wpm} WPM` : '—'}</TableCell>
                                                    <TableCell><Chip label={a.source || 'direct'} size="small" variant="outlined" /></TableCell>
                                                    <TableCell><Chip label={a.conversionState || 'unknown'} size="small" color={a.conversionState === 'subscription_started' ? 'success' : a.merged ? 'info' : 'default'} /></TableCell>
                                                    <TableCell>{a.merged ? '✓' : '—'}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </>
                    )}
                </Box>
            )}

            {/* ═══════════ TAB 8: USER JOURNEY ═══════════ */}
            {tab === 8 && (
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <TrendingUpIcon sx={{ color: cyan }} />
                            <Typography variant="h5">Post-Signup User Journey</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {[7, 14, 30, 90].map(d => (
                                <Button
                                    key={d}
                                    size="small"
                                    variant={journeyDays === d ? 'contained' : 'outlined'}
                                    onClick={() => { setJourneyDays(d); fetchJourneyData(d); }}
                                >
                                    {d}d
                                </Button>
                            ))}
                        </Box>
                    </Box>

                    {journeyLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
                    ) : !journeyData ? (
                        <Alert severity="info">No journey data available.</Alert>
                    ) : (
                        <>
                            {/* Funnel */}
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                Activation Funnel ({journeyData.funnel.signedUp} users signed up)
                            </Typography>
                            <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                                {[
                                    { key: 'signedUp', label: 'Signed Up', icon: '👤', color: '#1976d2' },
                                    { key: 'speedTestCompleted', label: 'Completed Speed Test', icon: '⚡', color: '#00BCD4' },
                                    { key: 'readingSessionStarted', label: 'Started Reading Session', icon: '📖', color: '#4CAF50' },
                                    { key: 'returnedNextDay', label: 'Returned Next Day', icon: '🔁', color: '#FF9800' },
                                    { key: 'openedOnboardingEmail', label: 'Opened Onboarding Email', icon: '📧', color: '#9C27B0' },
                                    { key: 'earnedBadge', label: 'Earned XP / Badge', icon: '🏆', color: '#FFD700' },
                                    { key: 'reachedPricing', label: 'Reached Pricing Page', icon: '💳', color: '#F44336' },
                                    { key: 'startedTrial', label: 'Started Trial', icon: '🚀', color: '#E91E63' },
                                ].map((step, i, arr) => {
                                    const count = journeyData.funnel[step.key];
                                    const total = journeyData.funnel.signedUp;
                                    const pct = total > 0 ? (count / total * 100).toFixed(1) : 0;
                                    const prevCount = i > 0 ? journeyData.funnel[arr[i - 1].key] : total;
                                    const stepPct = prevCount > 0 ? (count / prevCount * 100).toFixed(1) : 0;
                                    return (
                                        <Box key={step.key} sx={{ mb: 1.5 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <span style={{ fontSize: '1.1rem' }}>{step.icon}</span>
                                                    <strong>{step.label}</strong>
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>{count}</strong> / {total}
                                                    <span style={{ color: 'text.secondary', ml: 1 }}>({pct}%)</span>
                                                    {i > 0 && <span style={{ color: stepPct < 50 ? '#f44336' : 'text.secondary', ml: 1, fontSize: '0.75rem' }}>↓ {stepPct}%</span>}
                                                </Typography>
                                            </Box>
                                            <Box sx={{
                                                height: 8, borderRadius: 4, backgroundColor: 'grey.200',
                                                overflow: 'hidden',
                                            }}>
                                                <Box sx={{
                                                    height: '100%',
                                                    width: `${pct}%`,
                                                    backgroundColor: step.color,
                                                    borderRadius: 4,
                                                    transition: 'width 0.5s ease',
                                                }} />
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Paper>

                            {/* Per-user table */}
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                Recent Users ({journeyData.users.length})
                            </Typography>
                            <TableContainer component={Paper} sx={{ borderRadius: 2, maxHeight: 600 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>User</TableCell>
                                            <TableCell>Joined</TableCell>
                                            <TableCell>Source</TableCell>
                                            <TableCell align="center">Speed Test</TableCell>
                                            <TableCell align="center">Reading</TableCell>
                                            <TableCell align="center">Returned</TableCell>
                                            <TableCell align="center">Email Opened</TableCell>
                                            <TableCell align="center">XP/Badge</TableCell>
                                            <TableCell align="center">Pricing</TableCell>
                                            <TableCell align="center">Trial</TableCell>
                                            <TableCell>Sessions</TableCell>
                                            <TableCell>Sub</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {journeyData.users.map((u) => (
                                            <TableRow key={u.userId} hover>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={600}>{u.displayName}</Typography>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{u.email}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="caption">{new Date(u.createdAt).toLocaleDateString()}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {u.source ? <Chip label={u.source} size="small" variant="outlined" /> : <Typography variant="caption" sx={{ color: 'text.disabled' }}>—</Typography>}
                                                </TableCell>
                                                <TableCell align="center">{u.signals.speedTestCompleted ? '✅' : '—'}</TableCell>
                                                <TableCell align="center">{u.signals.readingSessionStarted ? '✅' : '—'}</TableCell>
                                                <TableCell align="center">{u.signals.returnedNextDay ? '✅' : '—'}</TableCell>
                                                <TableCell align="center">{u.signals.openedOnboardingEmail ? '✅' : '—'}</TableCell>
                                                <TableCell align="center">{u.signals.earnedBadge ? `${u.stats.xp} XP` : '—'}</TableCell>
                                                <TableCell align="center">{u.signals.reachedPricing ? '✅' : '—'}</TableCell>
                                                <TableCell align="center">{u.signals.startedTrial ? '✅' : '—'}</TableCell>
                                                <TableCell>{u.stats.sessionsCount || 0}</TableCell>
                                                <TableCell><StatusChip status={u.subscriptionStatus} /></TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </Box>
            )}
        </Container>
    );
};

// ── Review Books Manager Component ─────────────────────────────
const ReviewBooksManager = () => {
    const theme = useTheme();
    const [books, setBooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingBook, setEditingBook] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        coverImage: '',
        formats: [
            { format: 'ebook', asin: '' },
            { format: 'paperback', asin: '' },
            { format: 'hardcover', asin: '' },
        ],
    });
    const [saving, setSaving] = useState(false);
    const [coverUploading, setCoverUploading] = useState(false);
    const fileInputRef = React.useRef(null);

    const fetchBooks = useCallback(async () => {
        try {
            setLoading(true);
            const data = await reviewBooksAPI.getAllAdmin();
            setBooks(data.books || []);
        } catch (err) {
            setError('Failed to load review books.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchBooks(); }, [fetchBooks]);

    const resetForm = () => {
        setFormData({
            title: '',
            coverImage: '',
            formats: [
                { format: 'ebook', asin: '' },
                { format: 'paperback', asin: '' },
                { format: 'hardcover', asin: '' },
            ],
        });
        setEditingBook(null);
    };

    const handleOpenAdd = () => {
        resetForm();
        setDialogOpen(true);
    };

    const handleOpenEdit = (book) => {
        setEditingBook(book);
        setFormData({
            title: book.title,
            coverImage: book.coverImage || '',
            formats: ['ebook', 'paperback', 'hardcover'].map(fmt => {
                const existing = book.formats.find(f => f.format === fmt);
                return { format: fmt, asin: existing?.asin || '' };
            }),
        });
        setDialogOpen(true);
    };

    const handleClose = () => {
        if (saving) return;
        setDialogOpen(false);
        resetForm();
    };

    const handleSave = async () => {
        if (!formData.title.trim()) {
            setError('Title is required.');
            return;
        }
        const validFormats = formData.formats.filter(f => f.asin.trim());
        if (validFormats.length === 0) {
            setError('At least one format with an ASIN is required.');
            return;
        }

        setSaving(true);
        setError('');
        try {
            if (editingBook) {
                await reviewBooksAPI.update(editingBook._id, {
                    title: formData.title,
                    coverImage: formData.coverImage.trim(),
                    formats: validFormats,
                });
                setSuccess('Book updated.');
            } else {
                await reviewBooksAPI.create({
                    title: formData.title,
                    coverImage: formData.coverImage.trim(),
                    formats: validFormats,
                });
                setSuccess('Book created.');
            }
            setDialogOpen(false);
            resetForm();
            fetchBooks();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save book.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (bookId) => {
        if (!window.confirm('Delete this review book? This cannot be undone.')) return;
        try {
            await reviewBooksAPI.delete(bookId);
            fetchBooks();
            setSuccess('Book deleted.');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Failed to delete book.');
        }
    };

    const handleCoverUpload = async (bookId, file) => {
        if (!file) return;
        setCoverUploading(true);
        setError('');
        try {
            await reviewBooksAPI.uploadCover(bookId, file);
            fetchBooks();
            setSuccess('Cover uploaded.');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Failed to upload cover.');
        } finally {
            setCoverUploading(false);
        }
    };

    const FORMAT_LABELS = { ebook: 'eBook', paperback: 'Paperback', hardcover: 'Hardcover' };

    return (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <RateReviewIcon color="primary" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Review Books Manager
                    </Typography>
                </Box>
                <Button variant="contained" size="small" onClick={handleOpenAdd} startIcon={<PersonAddIcon />}>
                    Add Book
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : books.length === 0 ? (
                <Alert severity="info">No review books yet. Click "Add Book" to create one.</Alert>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Cover</TableCell>
                                <TableCell>Title</TableCell>
                                <TableCell>Formats</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {books.map((book) => (
                                <TableRow key={book._id} hover>
                                    <TableCell>
                                        {book.coverImage ? (
                                            <Box
                                                component="img"
                                                src={resolveCoverUrl(book.coverImage)}
                                                alt={book.title}
                                                sx={{ width: 40, height: 60, objectFit: 'cover', borderRadius: 1 }}
                                            />
                                        ) : (
                                            <Box sx={{ width: 40, height: 60, borderRadius: 1, bgcolor: alpha(cyan, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <MenuBookIcon sx={{ fontSize: 18, color: alpha(cyan, 0.4) }} />
                                            </Box>
                                        )}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            style={{ display: 'none' }}
                                            ref={(el) => { if (!fileInputRef.current) fileInputRef.current = {}; fileInputRef.current[book._id] = el; }}
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) handleCoverUpload(book._id, file);
                                                e.target.value = '';
                                            }}
                                        />
                                        <Button
                                            size="small"
                                            onClick={() => fileInputRef.current?.[book._id]?.click()}
                                            sx={{ display: 'block', fontSize: '0.7rem', mt: 0.5, minWidth: 0 }}
                                        >
                                            {coverUploading ? 'Uploading...' : 'Upload'}
                                        </Button>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>
                                        {book.title}
                                        {!book.coverImage && (
                                            <Chip label="No cover" size="small" color="warning" sx={{ ml: 1 }} />
                                        )}
                                        {book.formats.length === 0 && (
                                            <Chip label="No valid ASINs" size="small" color="error" sx={{ ml: 1 }} />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            {book.formats.map((f) => (
                                                <Chip
                                                    key={f.slug}
                                                    label={`${FORMAT_LABELS[f.format] || f.format} (${f.asin})`}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            ))}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => handleOpenEdit(book)} title="Edit">
                                            <Typography variant="caption" sx={{ fontWeight: 700 }}>Edit</Typography>
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDelete(book._id)} title="Delete" color="error">
                                            <Typography variant="caption" sx={{ fontWeight: 700 }}>Del</Typography>
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Add/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {editingBook ? 'Edit Review Book' : 'Add Review Book'}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Book Title"
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        sx={{ mt: 1, mb: 2 }}
                        placeholder="e.g. Read Faster Without Forgetting Everything"
                    />
                    <TextField
                        fullWidth
                        label="Cover Image URL"
                        value={formData.coverImage}
                        onChange={(e) => setFormData(prev => ({ ...prev, coverImage: e.target.value }))}
                        sx={{ mb: 1 }}
                        placeholder="/books/read-faster-without-forgetting-everything.png"
                        helperText="Prefer a static asset path like /books/<slug>.png (survives redeploys). File uploads to the server are wiped on redeploy."
                    />
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 2 }}>
                        Enter the ASIN for each available format:
                    </Typography>
                    {formData.formats.map((f, i) => (
                        <Box key={f.format} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                            <Box sx={{ width: 100, flexShrink: 0 }}>
                                <Chip label={FORMAT_LABELS[f.format]} size="small" sx={{ fontWeight: 600 }} />
                            </Box>
                            <TextField
                                fullWidth
                                size="small"
                                label="ASIN"
                                value={f.asin}
                                onChange={(e) => {
                                    const updated = [...formData.formats];
                                    updated[i] = { ...updated[i], asin: e.target.value };
                                    setFormData(prev => ({ ...prev, formats: updated }));
                                }}
                                placeholder="B0XXXXXXXX"
                                inputProps={{ maxLength: 10 }}
                            />
                        </Box>
                    ))}
                    <Alert severity="info" sx={{ mt: 1 }}>
                        Leave ASIN blank for formats that don't exist. Only formats with an ASIN will be shown on the review page.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" disabled={saving}>
                        {saving ? <CircularProgress size={20} /> : editingBook ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default AdminPanel;
